import { useEffect, useState } from "react";
import { T } from "react-translator-component";
import Thumbnail from "../../Phase2/Thumbnail";
import CommentForm from "./CommentForm";
import LikeDislikeButton from "../../LikeDislikeButton";
import { convertUserImage, displayUserName, displayUserNickname, isRegularAppUser } from "../../../util/utils";
import {
  Button,
  Typography,
  Avatar,
} from "@mui/material";
import { colors } from "../../../common/colors";
import profile from "../../../assets/images/profile_img.png";
import SafeTextWithLinks from "../../SafeTextWithLinks";

interface Props {
  currentUser: any;
  comment: any;
  canModify: boolean;
  onContinue: (
    text: string,
    editCommentId: number | null,
    parentCommentId: number | null
  ) => void;
  isReplyable?: boolean;
}

export default function CommentDetail(
  {
    currentUser,
    comment,
    canModify,
    onContinue,
    isReplyable = true
  }: Props
) {
  const [editCommentId, setEditCommentId] = useState<number | null>(null);
  const [replyCommentId, setReplyCommentId] = useState<number | null>(null);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [showReplayForm, setShowReplayForm] = useState<boolean>(false);

  useEffect(() => {
    handleCloseEditForm()
  }, [comment])

  const handleEdit = () => {
    setEditCommentId(comment.id);
    setReplyCommentId(null);
    setShowEditForm(true)
    setShowReplayForm(false)
  };

  const handleReply = () => {
    setEditCommentId(null);
    setReplyCommentId(comment.id);
    setShowReplayForm(true)
    setShowEditForm(false)
  };

  const handleCloseEditForm = () => {
    setEditCommentId(null);
    setReplyCommentId(null);
    setShowEditForm(false)
    setShowReplayForm(false)
  };

  const handleSubmit = (text: string) => {
    onContinue(text, editCommentId, replyCommentId)
  }

  const deletePhoto = (photo: any) => {
    // pass
  };

  const displayedUserIcon = () => {
    if (comment?.is_anonymous === 3) return profile

    return comment?.user?.image ? convertUserImage(comment.user.image) : profile
  }

  const displayedUserName = () => {
    let name = ""
    switch (comment.is_anonymous) {
      case 3:
        name = `${T("anonymous")}`
        break;
      case 2:
        name = displayUserName(comment?.user?.name)
        break;
      default:
        name = displayUserNickname(comment?.user?.nickname)
    }
    return name
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.userIcon}>
          <Avatar
            alt={comment.user_nickname}
            style={styles.avatar}
            src={displayedUserIcon()}
          />
        </div>

        <div style={styles.contentBox}>
          <Typography
            sx={styles.name}
            variant="body2"
          >
            {displayedUserName()}
          </Typography>

          <div>
            {comment?.images?.length !== 0 && (
              <Thumbnail deleteItem={deletePhoto} item={comment} type="comments" />
            )}

            {showEditForm ? (
              // コメントを編集するとき
              <CommentForm
                comment={comment?.content || ""}
                onSubmit={handleSubmit}
                onClose={() => handleCloseEditForm()}
                isEdit={true}
              />
            ) : (
              <SafeTextWithLinks text={comment?.content} />
            )}
          </div>

          {!showEditForm && !showReplayForm && (
            <div style={styles.btnContainer}>
              {canModify && comment?.user_id === currentUser?.id && (
                <Button
                  style={styles.button}
                  onClick={() => handleEdit()}
                >
                  {T("correct")}
                </Button>
              )}
              {canModify && isReplyable && isRegularAppUser(currentUser) && (
                <Button
                  style={styles.button}
                  onClick={() => handleReply()}
                >
                  {T("comment_reply")}
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={styles.favoriteContainer}>
          <LikeDislikeButton
            iconSize={"sm"}
            liketype={"comment"}
            item={comment}
            disabled={currentUser?.id === comment?.user_id || !canModify}
          />
        </div>
      </div>

      {showReplayForm && (
        // コメントに返信するとき
        <div style={styles.replayForm}>
          <CommentForm
            comment={""}
            onSubmit={handleSubmit}
            onClose={() => handleCloseEditForm()}
            isEdit={false}
          />
        </div>
      )}
    </>
  );
};

const styles = {
  container: {
    marginBottom: 16,
    display: "flex",
    width: "100%",
  },

  userIcon: {
    width: "40px",
  },

  contentBox: {
    width: "100%"
  },

  avatar: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    marginRight: "10px",
  },

  name: {
    color: colors.farm_black,
    fontSize: 12,
  },

  content: {
    userSelect: "text" as const,
    fontSize: 18,
    fontFamily: "mplus-1c-regular",
    wordWrap: "break-word" as const,
    margin: 0,
  },

  favoriteContainer: {
    width: "30px",
    paddingTop: "9px",
    textAlign: "center" as const,
  },

  btnContainer: {
    marginLeft: "-8px"
  },

  button: {
    fontSize: 12,
    color: colors.farm_gray200,
    padding: 0,
    width: "fit-content"as const,
  },

  replayForm: {
    paddingLeft: "40px"
  }
}